"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlusBold, PiMinusBold, PiArrowRightBold } from "react-icons/pi";

const DEFAULT_FAQS = [
  {
    q: "How Much Does It Cost?",
    a: "Hok is a task management platform designed for startups and growing teams. It helps you organize projects. They are devoted to delivering customized support and can provide you with an extensive estimate tailored to your unique needs.",
  },
  { q: "What Is Included In The Price?", a: "All plans include unlimited projects, team collaboration tools, priority support, and access to our full course library. Higher tiers unlock advanced analytics and custom branding." },
  { q: "Are There Any Discounts Available?", a: "Yes! We offer a 20% discount for annual subscriptions, and special pricing for educational institutions and non-profits. Contact us for more details." },
  { q: "How Can I Pay For It?", a: "We accept all major credit cards, PayPal, and bank transfers. All payments are processed securely through our payment gateway." },
  { q: "Is There A Money-Back Guarantee?", a: "Absolutely. We offer a full 30-day money-back guarantee, no questions asked. If you're not satisfied, we'll refund you completely." },
];

export default function HokFaq({ content, onContentChange, isSelected }: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const isEditing = !!onContentChange;

  const [openIndex, setOpenIndex] = useState<number>(0);

  const faqs = content.faqs || DEFAULT_FAQS;
  const sideImage = content.faqImage || "https://minimax-algeng-chat-tts-us.oss-us-east-1.aliyuncs.com/ccv2%2F2026-07-07%2FMiniMax-M2.7%2F2009335902018474192%2Ffb8ee8ec53883a38c40c62f72c16584abe635bce4e43a58525c437d57a1657ec..jpeg?Expires=1783506164&OSSAccessKeyId=LTAI5tCpJNKCf5EkQHSuL9xg&Signature=IVEz2G6sQGElWsruqpGezMQB%2Bg8%3D";

  const updateFaq = (idx: number, key: string, val: string) => {
    const updated = [...faqs];
    updated[idx] = { ...updated[idx], [key]: val };
    onContentChange?.({ ...content, faqs: updated });
  };

  const removeFaq = (idx: number) => {
    const updated = faqs.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...content, faqs: updated });
  };

  return (
    <section
      className={`relative py-16 sm:py-24 bg-white ${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* LEFT: Accordion */}
          <div className="space-y-3">
            {faqs.map((item: any, i: number) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen
                    ? "border-gray-200 shadow-md bg-white"
                    : "border-gray-100 bg-gray-50/80 hover:border-gray-200"
                    }`}
                >
                  {/* Header row */}
                  <div className="flex items-center gap-3 px-5 py-4">
                    {/* Question — editable inline or static, not wrapped in a button to avoid conflict */}
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <input
                          className="w-full text-sm sm:text-base font-semibold text-gray-900 bg-transparent outline-none border-b border-dashed border-gray-200 focus:border-purple-400 leading-snug"
                          value={item.q}
                          onChange={(e) => updateFaq(i, "q", e.target.value)}
                          placeholder="Question?"
                        />
                      ) : (
                        <button
                          onClick={() => setOpenIndex(isOpen ? -1 : i)}
                          className="w-full text-left text-sm sm:text-base font-semibold text-gray-900 leading-snug"
                        >
                          {item.q}
                        </button>
                      )}
                    </div>

                    {/* Toggle / delete buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isEditing && (
                        <button
                          onClick={() => removeFaq(i)}
                          className="w-6 h-6 rounded-full bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center text-xs font-semibold"
                          title="Remove"
                        >
                          ×
                        </button>
                      )}
                      <button
                        onClick={() => setOpenIndex(isOpen ? -1 : i)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${isOpen ? "text-white" : "bg-white text-gray-500 border border-gray-200"
                          }`}
                        style={isOpen ? { background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` } : {}}
                      >
                        {isOpen ? <PiMinusBold className="w-3.5 h-3.5" /> : <PiPlusBold className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Answer panel */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-72 opacity-100" : "max-h-0 opacity-0"
                      }`}
                  >
                    <div className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">
                      {isEditing ? (
                        <textarea
                          className="w-full text-sm text-gray-500 bg-transparent resize-none outline-none border-b border-dashed border-gray-200 focus:border-purple-400 leading-relaxed"
                          rows={3}
                          value={item.a}
                          onChange={(e) => updateFaq(i, "a", e.target.value)}
                          placeholder="Answer..."
                        />
                      ) : (
                        item.a
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add FAQ button (edit mode only) */}
            {isEditing && (
              <button
                onClick={() =>
                  onContentChange?.({
                    ...content,
                    faqs: [...faqs, { q: "New Question?", a: "Your answer here." }],
                  })
                }
                className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm font-semibold text-gray-400 hover:border-purple-300 hover:text-purple-500 transition-all"
              >
                + Add FAQ
              </button>
            )}
          </div>

          {/* RIGHT: Title + Image + CTA card */}
          <div className="flex flex-col gap-6">
            {/* Section heading */}
            <div>
              <span
                className="text-xs font-black uppercase tracking-[0.2em] mb-3 block"
                style={{ color: primaryColor }}
              >
                <EditableText
                  value={content.faqSubtitle || "FAQ"}
                  onChange={(v) => onContentChange?.({ ...content, faqSubtitle: v })}
                  as="span"
                  placeholder="FAQ"
                />
              </span>
              {/* Combined title — single editable unit */}
              <div className="flex flex-col">
                <h2 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold text-gray-900 tracking-tight leading-tight uppercase">
                  <EditableText
                    value={content.faqTitleLine1 || "Unlock Your True Potential"}
                    onChange={(v) => onContentChange?.({ ...content, faqTitleLine1: v })}
                    as="span"
                    placeholder="Main Headline"
                  />
                </h2>
                <h2 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold text-gray-900 tracking-tight leading-tight uppercase">
                  <EditableText
                    value={content.faqTitleLine2 || "With Our Guidance"}
                    onChange={(v) => onContentChange?.({ ...content, faqTitleLine2: v })}
                    as="span"
                    placeholder="Sub Headline"
                  />
                </h2>
              </div>
            </div>

            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
              <img
                src={sideImage}
                alt="FAQ Visual"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=700&h=500&fit=crop";
                }}
              />

              {/* Floating CTA card */}
              <div className="absolute bottom-4 left-4 right-4 bg-white rounded-2xl p-4 shadow-2xl flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-snug mb-0.5">
                    <EditableText
                      value={content.faqCtaTitle || "Still Have Questions?"}
                      onChange={(v) => onContentChange?.({ ...content, faqCtaTitle: v })}
                      as="span"
                      placeholder="Still Have Questions?"
                    />
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                    <EditableText
                      value={content.faqCtaSubtitle || "Feugiat pretium nibh ipsum consequat nisl pretium lectus quam. Aliquam ut porttitor leo a diam."}
                      onChange={(v) => onContentChange?.({ ...content, faqCtaSubtitle: v })}
                      as="span"
                      placeholder="Short description..."
                    />
                  </p>
                </div>
                <div
                  className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold text-white whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                >
                  <EditableText
                    value={content.faqCtaButton || "Get In Touch"}
                    onChange={(v) => onContentChange?.({ ...content, faqCtaButton: v })}
                    as="span"
                    placeholder="Get In Touch"
                  />
                  <PiArrowRightBold className="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
